import React, { useMemo } from 'react';
import styled from 'styled-components';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { AnalyticPanelState } from 'utils/types/AnalyticPanelModule';
import { ReportData } from 'utils/types/AnalyticPanelReport';

import { CostStructureSection } from './CostStructureSection';
import { MarginSection } from './MarginSection';
import { ReportHeader } from './ReportHeader';
import { StructureSection } from './StructureSection';
import { TimelineCostsChart } from './TimelineCostsChart';

const ReportContainer = styled.div`
    padding: 20px 120px;

    @media (max-width: 768px) {
        padding: 10px 20px;
    }
`;

const NoDataContainer = styled.div`
    text-align: center;
    padding: 60px 20px;
    background: var(--color-white);
    border-radius: var(--box-border-radius);
`;

const NoDataTitle = styled.h2`
    margin-bottom: 16px;
    color: var(--color-primary);
`;

const NoDataText = styled.p`
    color: var(--color-primary-70);
`;

interface AnalyticPanelReportProps {
    state: AnalyticPanelState;
}

export const hasReportRequiredData = (state: AnalyticPanelState) => {
    return stringToNumberOrZero(state.purchasePrice) > 0 && Boolean(state.costs) && Boolean(state.calculation);
};

export const AnalyticPanelReport: React.FC<AnalyticPanelReportProps> = ({ state }) => {
    const reportData: ReportData = useMemo(() => ({
        purchaseDate: state.purchaseDate,
        saleDate: state.saleDate,
        investmentTime: state.investmentTime,
        address: state.address,
        floor: state.floor,
        area: state.area,
        rooms: state.rooms,
        purchasePrice: state.purchasePrice,
        salePrice: state.salePrice,
        calculation: state.calculation,
        flip: state.flip,
        costs: state.costs
    }), [state]);

    if (!hasReportRequiredData(state)) {
        return (
            <ReportContainer>
                <NoDataContainer>
                    <NoDataTitle>Brak danych do wygenerowania raportu</NoDataTitle>
                    <NoDataText>
                        Wypełnij formularz i kliknij "Oblicz" aby wygenerować raport inwestycji.
                    </NoDataText>
                </NoDataContainer>
            </ReportContainer>
        );
    }

    return (
        <ReportContainer>
            <ReportHeader data={reportData} />

            <StructureSection data={reportData} />

            <MarginSection data={reportData} />

            <CostStructureSection data={reportData} />

            <TimelineCostsChart data={reportData} />
        </ReportContainer>
    );
};

export default AnalyticPanelReport;
